import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UTILS } from "utils";
import { API } from "api";
import { errorHandler } from "api/api/errorHandler";
import { useCurrentUserTempUrl } from "hooks/useCurrentUserItems";
import AppContainer from "components/ui/AppContainer";
import UnsavedChangesWarning from "components/UnsavedChangesWarning";
import AttachItemToModifier from "./AttachItemToModifier";
import { transformPayload } from "./UpdateItem";
import ItemsForm from "./ItemsForm";
import "./item.css";
import useGroupModifierApi from "features/Modifier/useGroupModifierApi";

const CreateItem = ({ inline = false, onCreated = null, onCancel = null }) => {
  // Hooks
  const router = useHistory();
  const { t } = useTranslation();
  // States
  const [item, setItem] = useState(UTILS.getItemModel());
  const [loading, setLoading] = useState(false);
  const [toBeAttachedCustomizations, setToBeAttachedCustomizations] = useState([]);
  const { linkModifierGroupsWithItems } = useGroupModifierApi();
  const [isDirty, setIsDirty] = useState(false);
  const { urls: data } = useCurrentUserTempUrl();

  /* A function that is used to set the basic info in the state. */
  const handleBasicInfo = (key, val) => {
    setItem((prevState) => ({
      ...prevState,
      values: {
        ...prevState.values,
        baseLanguage: {
          ...prevState.values.baseLanguage,
          [key]: val,
        },
      },
      errors: {
        ...prevState.errors,
        baseLanguage: {
          ...prevState.errors.baseLanguage,
          [key]: "",
        },
      },
    }));
    setIsDirty(true);
  };

  /* A function that is used to set the basic info in the state. */
  const handleValues = (key, val) => {
    setItem((prevState) => ({
      ...prevState,
      values: {
        ...prevState.values,
        [key]: val,
      },
      errors: {
        ...prevState.errors,
        [key]: "",
      },
    }));
    setIsDirty(true);
  };

  /* Validating the inputs. */
  const validateInputs = () => {
    let { objError, isValidForm } = UTILS.validateItemInfo(item);
    setItem((prevState) => ({
      values: {
        ...prevState.values,
      },
      errors: objError,
    }));
    return isValidForm;
  };

  const handleStockEnabledValues = (e) => {
    setItem((prevState) => {
      const { availableStock, lowStockThreshold } = prevState.values;

      return {
        ...prevState,
        values: {
          ...prevState.values,
          isStockEnabled: e?.target?.checked,
          availableStock: availableStock ?? 0, // if undefined, set to 0
          lowStockThreshold: lowStockThreshold ?? 0, // if undefined, set to 0
        },
        errors: {
          ...prevState.errors,
          isStockEnabled: "",
        },
      };
    });
    setIsDirty(true);
  };

  /**
   * An async function that adds an item to the database.
   */
  const addItem = async () => {
    try {
      if (!validateInputs()) return;
      setIsDirty(false);
      setLoading(true);
      if (validateInputs()) {
        const payload = transformPayload(item.values);
        const result = await API.ADD_ITEM(payload);
        if (result?.success === 1) {
          // await dele
        if(toBeAttachedCustomizations?.length > 0) {
          await linkModifierGroupsWithItems({itemId: result?.data?._id, attachedGroups: toBeAttachedCustomizations});
        }
          UTILS.successNotification(t("Item Added"));
          if (typeof onCreated === "function") {
            onCreated(result?.data);
          } else {
            router.push("/app/dashboard/items", {
              ...router?.location?.state,
            });
          }
        } else {
          UTILS.errorNotification(errorHandler(result?.message));
        }
      }
    } catch (error) {
      UTILS.errorNotification(errorHandler(error?.error?.message));
      setIsDirty(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (data?.imgUrls?.length > 0 || data?.videoUrls?.length > 0) {
      setItem({
        ...item,
        values: {
          ...item?.values,
          itemImageURLs:[...(data?.imgUrls ?? []), ...(data?.videoUrls ?? [])],
        },
      });
      setIsDirty(true);
    }
  }, [data]);

  return (
    <>
    <UnsavedChangesWarning hasUnsavedChanges={isDirty} />
      <AppContainer
        title={t("Create Item")}
        isModified={isDirty}
        loading={loading}
        subTitle={t("Please enter complete details for better user experience")}
        containerProps={{
          id: "appContainerItemForm",
        }}
      >
        <ItemsForm
          id="createItemForm"
          item={item}
          handleBasicInfo={handleBasicInfo}
          handleValues={handleValues}
          setItem={setItem}
          onSave={addItem}
          setIsDirty={setIsDirty}
          setLoading={setLoading}
          handleStockEnabledValues={handleStockEnabledValues}
          itemId={null}
          content={<AttachItemToModifier
            toBeAttachedCustomizations={toBeAttachedCustomizations}
            setToBeAttachedCustomizations={setToBeAttachedCustomizations}
            />}
          onBack={inline ? onCancel : null}
        />
      </AppContainer>
      </>
  );
};

export default CreateItem;
